import { useMutation, useQuery } from '@apollo/client'
import { Container, Paper, Stack } from '@mui/material'
import { SectionHeader } from '@sitoo/mui-components'
import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  AppSettingTokenStatusEnum,
  PaymentSpiAppId,
  SpiPaymentsPluginDocument,
  SpiPaymentsSettings,
  UpdateSpiPaymentsPluginDocument,
} from '../../../../generated/graphql'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { PluginsForm } from '../plugins-form'

type Form = SpiPaymentsSettings

export const SettingsVippsMobilePayPage = () => {
  const { t } = useTranslation(['apps', 'shared'])

  const { data } = useQuery(SpiPaymentsPluginDocument, {
    variables: {
      pluginId: PaymentSpiAppId.VippsMobilePay,
    },
  })

  const formContext = useForm<Form>({
    defaultValues: { token: '', url: '' },
  })

  const [updateSettings, { loading: isLoading }] = useMutation(
    UpdateSpiPaymentsPluginDocument,
  )

  const onSubmit = useCallback(
    async (formData: Form) => {
      const { token_status, ...rest } = formData
      const { data } = await updateSettings({
        variables: {
          input: {
            ...rest,
            id: PaymentSpiAppId.VippsMobilePay,
          },
        },
      })

      return data?.updateSpiPayments
    },
    [updateSettings],
  )

  const formData = data?.spiPaymentsSettings

  return (
    <Container>
      <FormProvider {...formContext}>
        <PluginsForm
          serverData={formData}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading}
        >
          <Paper>
            <SectionHeader variant="transparent">
              {t('apps:vipps_mobilepay.section_header')}
            </SectionHeader>

            <Stack gap={2} sx={{ p: 2, pt: 1 }}>
              <TextFieldInput
                name="url"
                control={formContext.control}
                label={t('apps:vipps_mobilepay.url_label')}
                helperText={t('apps:vipps_mobilepay.url_helper')}
                placeholder={t('apps:vipps_mobilepay.url_placeholder')}
                fullWidth
                rules={{
                  validate: (value) => {
                    if (!value && formContext.watch('token')) {
                      return t('apps:vipps_mobilepay.invalid_fields', {
                        url: t('apps:vipps_mobilepay.url_label'),
                        token: t('apps:vipps_mobilepay.token_label'),
                      })
                    }
                    if (value && !/^https?:\/\//.test(value)) {
                      return t('shared:validation.field_invalid', {
                        field: t('apps:vipps_mobilepay.url_label'),
                      })
                    }
                  },
                }}
              />

              <TextFieldInput
                name="token"
                control={formContext.control}
                type="password"
                label={t('apps:vipps_mobilepay.token_label')}
                helperText={t('apps:vipps_mobilepay.token_helper')}
                placeholder={
                  formData?.token_status === AppSettingTokenStatusEnum.Set
                    ? t('apps:vipps_mobilepay.replace_token_placeholder')
                    : t('apps:vipps_mobilepay.token_placeholder')
                }
                fullWidth
                rules={{
                  validate: (value) => {
                    if (!value && formContext.watch('url')) {
                      return t('apps:vipps_mobilepay.invalid_fields', {
                        url: t('apps:vipps_mobilepay.url_label'),
                        token: t('apps:vipps_mobilepay.token_label'),
                      })
                    }
                  },
                }}
              />
            </Stack>
          </Paper>
        </PluginsForm>
      </FormProvider>
    </Container>
  )
}
